import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getKinderhuis, deleteKinderhuis, createKinderhuis, updateKinderhuis } from "../../../utils/api";

const columns = [
  { key: "name", label: "Name" },
  { key: "location", label: "Location" },
  { key: "capacity", label: "Capacity" },
  { 
    key: "availibility", 
    label: "Availability",
    render: (value) => value ? "Available" : "Not Available"
  },
  { 
    key: "currentChildren", 
    label: "Current Children",
    render: (value) => value || 0
  },
];

const filters = [
  {
    key: "availibility",
    label: "Filter by Availability",
    type: "select",
    options: [
      { value: "true", label: "Available" },
      { value: "false", label: "Not Available" },
    ]
  },
];

const initialFormData = {
  name: "",
  description: "",
  location: "",
  contact: "",
  availibility: true,
  capacity: 0,
  currentChildren: 0,
  adres: {
    street: "",
    city: "",
    state: "",
    country: ""
  },
  facilities: "",
  established: "",
  imageUrl: ""
};

export default function Kinderhuis() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      location: item.location || "",
      contact: item.contact || "",
      availibility: item.availibility ?? true,
      capacity: item.capacity || 0,
      currentChildren: item.currentChildren || 0,
      adres: item.adres || { street: "", city: "", state: "", country: "" },
      facilities: item.facilities || "",
      established: item.established || "",
      imageUrl: item.imageUrl || ""
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => {
    handleEdit(item);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("adres.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({ 
        ...prev, 
        adres: { ...prev.adres, [field]: value }
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === "checkbox" ? checked : value
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && selectedItem) {
        await updateKinderhuis(selectedItem._id, formData);
        alert("Kinderhuis updated successfully!");
      } else {
        await createKinderhuis(formData);
        alert("Kinderhuis created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Kinderhuis"
          fetchData={getKinderhuis}
          deleteData={deleteKinderhuis}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search kinderhuis..."
        />
      </main>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Kinderhuis" : "Add New Kinderhuis"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                placeholder="Enter name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location & Contact</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  placeholder="Enter location"
                />
              </div>
              <div className="form-group">
                <label>Contact</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleFormChange}
                  placeholder="Enter contact"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Street</label>
                <input
                  type="text"
                  name="adres.street"
                  value={formData.adres.street}
                  onChange={handleFormChange}
                  placeholder="Street"
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="adres.city"
                  value={formData.adres.city}
                  onChange={handleFormChange}
                  placeholder="City"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="adres.state"
                  value={formData.adres.state}
                  onChange={handleFormChange}
                  placeholder="State"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="adres.country"
                  value={formData.adres.country}
                  onChange={handleFormChange}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Capacity</label>
                <input
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleFormChange}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Current Children</label>
                <input
                  type="number"
                  name="currentChildren"
                  value={formData.currentChildren}
                  onChange={handleFormChange}
                  min="0"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Established</label>
                <input
                  type="text"
                  name="established"
                  value={formData.established}
                  onChange={handleFormChange}
                  placeholder="Year established"
                />
              </div>
              <div className="form-group">
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    name="availibility"
                    checked={formData.availibility}
                    onChange={handleFormChange}
                  />
                  Available
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Facilities</label>
              <textarea
                name="facilities"
                value={formData.facilities}
                onChange={handleFormChange}
                rows="2"
                placeholder="List facilities"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Image</h3>
            <div className="form-group">
              <label>Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update" : "Create")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
